/**
 * arbi login [--email <email>] [--password <password>] [--signing-key <key>]
 *
 * Authenticates via SDK, stores signing key.
 * Supports password login, SSO login, or signing-key login (for agent recovery).
 * If email/password not provided via flags or env vars, prompts interactively.
 * After login, shows interactive workspace picker.
 */

import { Command } from 'commander'
import 'fake-indexeddb/auto'
import {
  performPasswordLogin,
  performSigningKeyLogin,
  performSsoDeviceFlowLogin,
  formatWorkspaceChoices,
} from '@arbidocs/sdk'
import { success, error, dim, ref } from '../output.js'
import { store, updateConfig, getConfig, clearChatSession } from '../config.js'
import { promptInput, promptPassword, promptSelect } from '../prompts.js'
import { checkForUpdates } from '../version-check.js'
import { runAction, formatCliError } from '../helpers.js'
import { updateCompletionCache } from '../completion-cache.js'
import { setupAgent, startListening } from './listen.js'

export function registerLoginCommand(program: Command): void {
  program
    .command('login')
    .description('Log in to ARBI')
    .option('-e, --email <email>', 'Email address (or ARBI_EMAIL env var)')
    .option('-p, --password <password>', 'Password (or ARBI_PASSWORD env var)')
    .option('-k, --signing-key <key>', 'Signing key (base64) for agent recovery login')
    .option('-w, --workspace <id>', 'Workspace ID to select after login')
    .option('--sso', 'Log in with Auth0 SSO (device flow)')
    .option('--agent <name>', 'Agent backend to configure (claude, openclaw)')
    .option('--listen', 'Start DM listener after login')
    .option('--json', 'Emit a single JSON object on success (scripting)')
    .action(
      (opts: {
        email?: string
        password?: string
        signingKey?: string
        workspace?: string
        sso?: boolean
        agent?: string
        listen?: boolean
        json?: boolean
      }) =>
        runAction(async () => {
          const config = store.requireConfig()

          // Guard: non-TTY stdin + missing credentials → print a clear error
          // instead of inquirer's confusing "User force closed the prompt with 0 null".
          const isTty = process.stdin.isTTY
          const emailFromFlagOrEnv = opts.email || process.env.ARBI_EMAIL
          if (!isTty && !emailFromFlagOrEnv) {
            const msg =
              'Email is required when stdin is not a TTY. Use --email <email> or set ARBI_EMAIL.'
            if (opts.json) console.log(JSON.stringify({ ok: false, error: msg }))
            else error(msg)
            process.exit(1)
          }
          const passwordFromFlagOrEnv = opts.password || process.env.ARBI_PASSWORD
          if (!isTty && !passwordFromFlagOrEnv && !opts.signingKey) {
            // `--sso` still needs the master password: ARBI is E2E-encrypted,
            // and the password is the seed for the user's encryption keys.
            // The SSO token only authenticates the user with Auth0; it can't
            // unlock the user's E2E vault on its own. Surface that here so
            // an agent doesn't have to read the source to learn why.
            const why = opts.sso
              ? '\nNote: --sso still requires --password — the master password is the E2E key seed,\n' +
                'the SSO token only proves identity. ARBI has no passwordless mode.'
              : ''
            const msg =
              'Password is required when stdin is not a TTY. Use --password <password> or set ARBI_PASSWORD.' +
              why
            if (opts.json) console.log(JSON.stringify({ ok: false, error: msg }))
            else error(msg)
            process.exit(1)
          }

          // Resolve email: flag > env var > interactive prompt
          const email = emailFromFlagOrEnv || (await promptInput('Email'))

          try {
            let ssoPolling = false
            const { arbi } = opts.signingKey
              ? await performSigningKeyLogin(config, email, opts.signingKey, store)
              : opts.sso
                ? await (async () => {
                    const pw = passwordFromFlagOrEnv || (await promptPassword('Password'))
                    const result = await performSsoDeviceFlowLogin(config, email, pw, store, {
                      onUserCode: (userCode: string, verificationUri: string) => {
                        console.log(`\nOpen this URL in your browser:\n  ${verificationUri}\n`)
                        console.log(`And enter code: ${userCode}\n`)
                        console.log('Waiting for authorization...')
                        ssoPolling = true
                      },
                      onPoll: () => {
                        process.stdout.write('.')
                      },
                    })
                    if (ssoPolling) console.log('\n')
                    return result
                  })()
                : await (async () => {
                    const pw = passwordFromFlagOrEnv || (await promptPassword('Password'))
                    return performPasswordLogin(config, email, pw, store)
                  })()

            // Confirm authentication succeeded (human-readable mode only)
            if (!opts.json) success(`Logged in as ${email}`)

            // Clear stale chat session from previous login
            clearChatSession()

            const { data: workspaces } = await arbi.fetch.GET('/v1/user/workspaces')
            const wsList = workspaces || []
            updateCompletionCache(wsList)

            // Filter to workspaces where user has a role (member of workspace)
            const memberWorkspaces = wsList.filter((ws) =>
              ws.users?.some((u) => u.user.email === email)
            )

            let selectedWorkspace: { external_id: string; name: string } | undefined

            if (memberWorkspaces.length === 0) {
              if (!opts.json) {
                console.log('No workspaces found. Create one with: arbi workspace create <name>')
              }
            } else if (opts.workspace) {
              const ws = memberWorkspaces.find((w) => w.external_id === opts.workspace)
              if (!ws) {
                const msg = `Workspace ${opts.workspace} not found or you don't have access.`
                if (opts.json) console.log(JSON.stringify({ ok: false, error: msg }))
                else error(msg)
                process.exit(1)
              }
              updateConfig({ selectedWorkspaceId: ws.external_id })
              selectedWorkspace = { external_id: ws.external_id, name: ws.name }
              if (!opts.json) success(`Workspace: ${ws.name} (${ref(ws.external_id)})`)
            } else if (memberWorkspaces.length === 1) {
              updateConfig({ selectedWorkspaceId: memberWorkspaces[0].external_id })
              selectedWorkspace = {
                external_id: memberWorkspaces[0].external_id,
                name: memberWorkspaces[0].name,
              }
              if (!opts.json)
                success(
                  `Workspace: ${memberWorkspaces[0].name} (${ref(memberWorkspaces[0].external_id)})`
                )
            } else if (opts.json) {
              // JSON mode with multiple workspaces: don't prompt; caller picks
              // via --workspace or `arbi workspace select`.
            } else {
              const choices = formatWorkspaceChoices(memberWorkspaces)
              const selected = await promptSelect('Select workspace', choices)
              updateConfig({ selectedWorkspaceId: selected })
              const ws = memberWorkspaces.find((w) => w.external_id === selected)!
              selectedWorkspace = { external_id: selected, name: ws.name }
              success(`Workspace: ${ws.name} (${ref(selected)})`)
              dim('\nTip: Run "arbi config alias" to use A as a shortcut for "arbi ask"')
            }

            // Agent setup + listener (optional)
            if (opts.agent || opts.listen) {
              await setupAgent(opts.agent, config)
              if (opts.listen) {
                await startListening(opts.agent, config)
              }
            }

            if (opts.json) {
              console.log(
                JSON.stringify({
                  ok: true,
                  email,
                  server: config.baseUrl,
                  workspace: selectedWorkspace ?? null,
                  workspaces_available: memberWorkspaces.length,
                })
              )
            }
          } catch (err) {
            const msg = formatCliError(err)
            if (opts.json) console.log(JSON.stringify({ ok: false, error: msg }))
            else error(`Login failed: ${msg}`)
            process.exit(1)
          } finally {
            await checkForUpdates(getConfig()?.autoUpdate)
          }
        })()
    )
}
